import { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Flex,
  Group,
  Paper,
  Radio,
  Select,
  Stack,
  Textarea,
  TextInput,
} from '@mantine/core'
import { useForm, Controller, FormProvider } from 'react-hook-form'
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema'
import {
  IconCheck,
  IconPencil,
  IconPlayerPauseFilled,
  IconPlayerPlayFilled,
} from '@tabler/icons-react'
import type { DocumentReference, Patient } from '@medplum/fhirtypes'
import { logError } from '@/utils/errorUtils'
import {
  CareCoordinatorNoteFormValues,
  careCoordinatorNoteSchema,
  encounterTypeOptions,
  outcomeOptions,
  reasonForCallOptions,
} from './careCoordinatorSchema'
import { useSubmitCareCoordinatorNote } from './useSubmitCareCoordinatorNote'
import { useUpdateCareCoordinatorNote } from './useUpdateCareCoordinatorNote'

const CARE_COORDINATOR_NOTE_DATA_URL =
  'http://behuman.com/fhir/StructureDefinition/care-coordinator-note-data'

function parseStoredValues(docRef: DocumentReference): Partial<CareCoordinatorNoteFormValues> {
  const ext = docRef.extension?.find((e) => e.url === CARE_COORDINATOR_NOTE_DATA_URL)
  if (!ext?.valueString) return {}
  try {
    return JSON.parse(ext.valueString) as Partial<CareCoordinatorNoteFormValues>
  } catch {
    return {}
  }
}

type Props = {
  patient: Patient
  onSubmitted: (reference?: string) => void
  onCancel: () => void
  documentReference?: DocumentReference
}

function formatMmSs(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60)
  const s = Math.abs(totalSeconds % 60)
  return `${m}:${s.toString().padStart(2, '0')}`
}

function parseMmSs(input: string): number | undefined {
  const trimmed = input.trim()
  if (!trimmed) {
    return undefined
  }
  const match = trimmed.match(/^(\d+):([0-5]?\d)$/)
  if (match) {
    return parseInt(match[1], 10) * 60 + parseInt(match[2], 10)
  }
  const asNumber = Number(trimmed)
  if (Number.isFinite(asNumber) && asNumber >= 0) {
    return Math.round(asNumber)
  }
  return undefined
}

export function CareCoordinatorNote({ patient, onSubmitted, onCancel, documentReference }: Props) {
  const isEditMode = Boolean(documentReference)
  const storedValues = documentReference ? parseStoredValues(documentReference) : {}

  const submitMutation = useSubmitCareCoordinatorNote(patient, onSubmitted)
  const updateMutation = useUpdateCareCoordinatorNote(
    patient,
    documentReference ?? ({} as DocumentReference),
    onSubmitted,
  )
  const activeMutation = isEditMode ? updateMutation : submitMutation

  const form = useForm<CareCoordinatorNoteFormValues>({
    resolver: standardSchemaResolver(careCoordinatorNoteSchema),
    defaultValues: {
      encounterType: storedValues.encounterType ?? 'phone-call',
      reasonForCall: storedValues.reasonForCall ?? 'result-follow-up',
      callDurationSeconds: storedValues.callDurationSeconds ?? 0,
      quickNote: storedValues.quickNote ?? '',
      outcome: storedValues.outcome ?? 'completed',
      followUpNeeded: storedValues.followUpNeeded ?? 'no',
      followUpDate: storedValues.followUpDate ?? '',
    },
  })

  const [durationEditing, setDurationEditing] = useState(false)
  const [durationDraft, setDurationDraft] = useState('0:00')
  const [durationPaused, setDurationPaused] = useState(isEditMode)

  const callDurationSeconds = form.watch('callDurationSeconds')
  const followUpNeeded = form.watch('followUpNeeded')

  useEffect(() => {
    if (durationEditing || durationPaused) {
      return
    }
    const id = window.setInterval(() => {
      form.setValue('callDurationSeconds', (form.getValues('callDurationSeconds') || 0) + 1, {
        shouldDirty: false,
        shouldValidate: false,
      })
    }, 1000)
    return () => window.clearInterval(id)
  }, [durationEditing, durationPaused, form])

  useEffect(() => {
    if (followUpNeeded === 'no') {
      form.setValue('followUpDate', '', { shouldValidate: true })
    }
  }, [followUpNeeded, form])

  const onSubmit = form.handleSubmit(
    (values) => activeMutation.mutateAsync(values),
    (err) => logError('patient', 'Care Coordinator note validation failed', err),
  )

  const encounterTypeData = useMemo(
    () => encounterTypeOptions.map((o) => ({ value: o.value, label: o.label })),
    [],
  )
  const reasonData = useMemo(
    () => reasonForCallOptions.map((o) => ({ value: o.value, label: o.label })),
    [],
  )
  const outcomeData = useMemo(
    () => outcomeOptions.map((o) => ({ value: o.value, label: o.label })),
    [],
  )

  return (
    <FormProvider {...form}>
      <Box>
        {/* Title lives in the PatientSidePanel header now (PD-1163), so it stays
            visible in fullscreen; no duplicate heading here. */}
        <Alert color="red" mb="md">
          <span className="font-semibold">Note:</span> The content of this note may be shared with
          the patient and external providers, so document carefully and professionally.
        </Alert>

        <form onSubmit={onSubmit}>
          <Paper withBorder radius="md" p="md" mb="md">
            <p className="mb-3 text-xs font-semibold tracking-wider text-[#6B7280] uppercase">
              Encounter Basics
            </p>
            <Group grow align="flex-start" mb="md" wrap="nowrap">
              <Controller
                control={form.control}
                name="encounterType"
                render={({ field, fieldState }) => (
                  <Select
                    label="Encounter Type"
                    data={encounterTypeData}
                    value={field.value}
                    onChange={(v) => v && field.onChange(v)}
                    error={fieldState.error?.message}
                    allowDeselect={false}
                  />
                )}
              />
              <Controller
                control={form.control}
                name="reasonForCall"
                render={({ field, fieldState }) => (
                  <Select
                    label="Reason for Call"
                    data={reasonData}
                    value={field.value}
                    onChange={(v) => v && field.onChange(v)}
                    error={fieldState.error?.message}
                    allowDeselect={false}
                  />
                )}
              />
            </Group>

            <Stack gap={4}>
              <label className="text-sm font-medium">Call Duration</label>
              {!durationEditing ? (
                <Paper withBorder radius="sm" p="xs" bg="#EEF4FF">
                  <Group justify="space-between" wrap="nowrap">
                    <Group gap={8} wrap="nowrap">
                      <span className="font-mono font-semibold text-[#1E40AF]">
                        {formatMmSs(callDurationSeconds)}
                      </span>
                      <span className="text-sm text-[#3B82F6]">
                        {durationPaused ? 'Paused' : 'Recording...'}
                      </span>
                    </Group>
                    <Group gap={4} wrap="nowrap">
                      <button
                        type="button"
                        aria-label={durationPaused ? 'Resume recording' : 'Pause recording'}
                        className="text-[#6B7280] hover:text-[#111827]"
                        onClick={() => setDurationPaused((p) => !p)}
                      >
                        {durationPaused ? (
                          <IconPlayerPlayFilled size={16} />
                        ) : (
                          <IconPlayerPauseFilled size={16} />
                        )}
                      </button>
                      <button
                        type="button"
                        aria-label="Edit call duration"
                        className="text-[#6B7280] hover:text-[#111827]"
                        onClick={() => {
                          setDurationDraft(formatMmSs(callDurationSeconds))
                          setDurationEditing(true)
                        }}
                      >
                        <IconPencil size={16} />
                      </button>
                    </Group>
                  </Group>
                </Paper>
              ) : (
                <Group gap={8} wrap="nowrap">
                  <TextInput
                    style={{ flex: 1 }}
                    placeholder="M:SS"
                    value={durationDraft}
                    onChange={(e) => setDurationDraft(e.currentTarget.value)}
                    autoFocus
                  />
                  <button
                    type="button"
                    aria-label="Save call duration"
                    className="rounded-md bg-[#3B82F6] p-2 text-white hover:bg-[#2563EB]"
                    onClick={() => {
                      const parsed = parseMmSs(durationDraft)
                      if (parsed !== undefined) {
                        form.setValue('callDurationSeconds', parsed, { shouldDirty: true })
                      }
                      setDurationEditing(false)
                    }}
                  >
                    <IconCheck size={16} />
                  </button>
                </Group>
              )}
            </Stack>
          </Paper>

          <Paper withBorder radius="md" p="md" mb="md">
            <p className="mb-3 text-xs font-semibold tracking-wider text-[#6B7280] uppercase">
              Quick Note
            </p>
            <Controller
              control={form.control}
              name="quickNote"
              render={({ field, fieldState }) => (
                <Textarea
                  placeholder="Brief note about discussion and next steps..."
                  minRows={4}
                  autosize
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  error={fieldState.error?.message}
                />
              )}
            />
          </Paper>

          <Paper withBorder radius="md" p="md" mb="md">
            <p className="mb-3 text-xs font-semibold tracking-wider text-[#6B7280] uppercase">
              Follow-up
            </p>
            <Group grow align="flex-start" mb="md" wrap="nowrap">
              <Controller
                control={form.control}
                name="outcome"
                render={({ field, fieldState }) => (
                  <Select
                    label="Outcome"
                    data={outcomeData}
                    value={field.value}
                    onChange={(v) => v && field.onChange(v)}
                    error={fieldState.error?.message}
                    allowDeselect={false}
                  />
                )}
              />
              <Controller
                control={form.control}
                name="followUpNeeded"
                render={({ field }) => (
                  <Stack gap={4}>
                    <label className="text-sm font-medium">Follow-up Needed</label>
                    <Radio.Group value={field.value} onChange={field.onChange}>
                      <Group gap="md" mt={6}>
                        <Radio value="yes" label="Yes" />
                        <Radio value="no" label="No" />
                      </Group>
                    </Radio.Group>
                  </Stack>
                )}
              />
            </Group>

            {followUpNeeded === 'yes' && (
              <Controller
                control={form.control}
                name="followUpDate"
                render={({ field, fieldState }) => (
                  <Stack gap={4}>
                    <label className="text-sm font-medium">Follow-up Date</label>
                    <input
                      type="date"
                      className="w-full rounded-md border border-[#ced4da] px-3 py-2 text-sm"
                      value={field.value || ''}
                      onChange={(e) => field.onChange(e.currentTarget.value)}
                      onBlur={field.onBlur}
                    />
                    {fieldState.error?.message && (
                      <span className="text-xs text-red-600">{fieldState.error.message}</span>
                    )}
                  </Stack>
                )}
              />
            )}
          </Paper>

          {/* -24px bleeds past PatientSidePanel's p-6 (24px) so the sticky footer spans the panel width */}
          <Flex
            justify="flex-end"
            gap="sm"
            style={{
              position: 'sticky',
              bottom: 0,
              // Own stacking context so scrolling fields (e.g. the Select
              // chevron) hide behind the bar instead of bleeding over it.
              zIndex: 2,
              margin: '16px -24px 0',
              padding: '12px 24px',
              background: '#FBFBFB',
              borderTop: '1px solid #EEEEEE',
            }}
          >
            <Button
              variant="default"
              type="button"
              onClick={onCancel}
              disabled={form.formState.isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" color="green" loading={form.formState.isSubmitting}>
              {isEditMode ? 'Update Note' : 'Submit Note'}
            </Button>
          </Flex>
        </form>
      </Box>
    </FormProvider>
  )
}
